// MED-1268: the Create Cohort wizard saves its draft in localStorage. The
// draft can hold a list of time slots that did not save, so it belongs to one
// user. The key holds the user id, and logout removes every wizard draft.

// The old key was shared by every user of the browser. A draft under it cannot
// be tied to a user, so it is removed and never read.
export const LEGACY_WIZARD_DRAFT_KEY = 'cohort-wizard-draft-v2';
const WIZARD_DRAFT_KEY_PREFIX = 'cohort-wizard-draft-v3:';
// Matches the old key and every per-user key.
const WIZARD_DRAFT_KEY_FAMILY = 'cohort-wizard-draft-';

export type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

// The cohort exists, but some time slots were not saved.
export interface TimeSlotRecovery {
  cohortId: string;
  cohortName: string;
  unsavedSlotIds: string[];
  // How many times the slots were sent, and when the last try ran (ISO).
  attempts?: number;
  lastTriedAt?: string;
}

export interface PendingTimeSlotRecovery<Slot> {
  recovery: TimeSlotRecovery;
  timeSlots: Slot[];
}

// No user, no key: the wizard then keeps no draft.
export function wizardDraftKey(userId: string | null | undefined): string | null {
  return userId ? `${WIZARD_DRAFT_KEY_PREFIX}${userId}` : null;
}

export function readWizardDraftJson(storage: DraftStorage, userId: string | null | undefined): string | null {
  try {
    storage.removeItem(LEGACY_WIZARD_DRAFT_KEY);
    const key = wizardDraftKey(userId);
    return key ? storage.getItem(key) : null;
  } catch {
    return null;
  }
}

export function writeWizardDraftJson(storage: DraftStorage, userId: string | null | undefined, json: string): void {
  const key = wizardDraftKey(userId);
  if (!key) return;
  try {
    storage.setItem(key, json);
  } catch (e) {
    console.error('Failed to save draft:', e);
  }
}

export function removeWizardDraft(storage: DraftStorage, userId: string | null | undefined): void {
  const key = wizardDraftKey(userId);
  if (!key) return;
  try {
    storage.removeItem(key);
  } catch {
    // Storage can be blocked. Nothing to remove then.
  }
}

// Logout: remove the wizard draft of every user, and the old shared draft.
export function clearAllWizardDrafts(storage: DraftStorage): void {
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (key?.startsWith(WIZARD_DRAFT_KEY_FAMILY)) keys.push(key);
    }
    keys.forEach(key => storage.removeItem(key));
  } catch {
    // Storage can be blocked. Nothing to remove then.
  }
}

function parseRecovery<Slot>(json: string | null): PendingTimeSlotRecovery<Slot> | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    const recovery = parsed?.timeSlotRecovery;
    if (!recovery?.cohortId || !Array.isArray(recovery.unsavedSlotIds)) return null;
    const timeSlots = Array.isArray(parsed?.values?.timeSlots) ? parsed.values.timeSlots : [];
    return { recovery, timeSlots };
  } catch {
    return null;
  }
}

// The cohort page shows a notice while the user has a list of unsaved time
// slots for that cohort.
export function pendingTimeSlotRecovery<Slot = unknown>(
  storage: DraftStorage,
  userId: string | null | undefined,
  cohortId: string,
): PendingTimeSlotRecovery<Slot> | null {
  const pending = parseRecovery<Slot>(readWizardDraftJson(storage, userId));
  return pending && pending.recovery.cohortId === cohortId ? pending : null;
}

// The cohort was deleted. Its list of unsaved time slots can never be saved,
// so the draft that holds it is removed. Returns true when a draft was removed.
export function clearTimeSlotRecoveryForCohort(
  storage: DraftStorage,
  userId: string | null | undefined,
  cohortId: string,
): boolean {
  if (!pendingTimeSlotRecovery(storage, userId, cohortId)) return false;
  removeWizardDraft(storage, userId);
  return true;
}
